<template>
  <div :class="$style.wrapper">
    <!-- Хедер с поиском -->
    <div :class="$style.searchSection">
      <div :class="$style.headerRow">
        <div :class="$style.sectionLabel">Название модели или артикул</div>
      </div>

      <div :class="$style.searchCard">
        <input
          v-model="search"
          type="text"
          :class="$style.searchInput"
          placeholder="Поиск"
          @input="handleSearchInput"
          autofocus
        />
        <button
          v-if="search"
          type="button"
          :class="$style.clearBtn"
          @click="clearSearch"
        >
          ✕
        </button>
      </div>
    </div>

    <section
      ref="listSection"
      :class="$style.listSection"
      @scroll="handleScroll"
    >
      <!-- Лоадер -->
      <div
        v-if="isLoading && filteredModels.length === 0"
        :class="$style.loadingState"
      >
        <div :class="$style.skeletonCard">
          <div class="skeleton" :class="$style.skeletonLine"></div>
          <div class="skeleton" :class="$style.skeletonLineShort"></div>
        </div>
        <div :class="$style.skeletonCard">
          <div class="skeleton" :class="$style.skeletonLine"></div>
          <div class="skeleton" :class="$style.skeletonLineShort"></div>
        </div>
      </div>

      <template v-else>
        <p
          v-if="!filteredModels.length && !isLoadingMore"
          :class="$style.emptyState"
        >
          Ничего не найдено
        </p>

        <!-- Единый блок со списком -->
        <div :class="$style.resultsGroup">
          <!-- Своя модель (если её нет в справочнике) -->
          <div :class="$style.modelRow" @click="addCustomModel">
            <div :class="$style.modelInfo">
              <div :class="$style.addTitle">➕ Добавить свою модель</div>
              <div :class="$style.modelSub">
                Нет в справочнике? Создайте свою — отправится на модерацию.
              </div>
            </div>
            <div :class="$style.chevron">›</div>
          </div>

          <!-- Разделитель -->
          <div
            v-if="filteredModels.length"
            :class="$style.rowDivider"
          ></div>

          <div
            v-for="(model, index) in filteredModels"
            :key="model.id"
            :class="$style.modelRow"
            @click="selectAndClose(model)"
          >
            <div :class="$style.modelInfo">
              <div :class="$style.modelTitle">{{ model.title }}</div>
              <div :class="$style.modelSub">
                <!-- Формируем подпись: "Color" | SKU -->
                <span v-if="model.colour">"{{ model.colour }}"</span>
                <span v-else-if="model.brand">"{{ model.brand }}"</span>

                <span v-if="model.article" :class="$style.skuDivider">
                  | {{ model.article }}
                </span>
                <span v-else :class="$style.skuDivider">
                  | ID: {{ model.id }}
                </span>
              </div>
            </div>

            <div :class="$style.radioWrapper">
              <div
                :class="[
                  $style.radioCircle,
                  String(selectedId) === String(model.id) &&
                    $style.radioChecked,
                ]"
              ></div>
            </div>

            <!-- Разделитель -->
            <div
              v-if="index < filteredModels.length - 1"
              :class="$style.rowDivider"
            ></div>
          </div>
        </div>

        <!-- Лоадер для подгрузки -->
        <div v-if="isLoadingMore" :class="$style.loadingMore">
          <div :class="$style.loadingSpinner"></div>
          <div :class="$style.loadingText">Загрузка новых моделей...</div>
        </div>
      </template>
    </section>
  </div>
</template>

<script>
export default {
  name: "SelectModel",
  data() {
    return {
      isLoading: true,
      isLoadingMore: false,
      filteredModels: [],
      search: "",
      searchTimer: null,
      scrollTimer: null,
      selectedId: this.$store.state.selectedModel?.id
        ? String(this.$store.state.selectedModel.id)
        : null,
      offset: 0,
      limit: 20,
      hasMore: true,
      intersectionObserver: null,
      isRequestPending: false,
      lastScrollTime: 0,
    }
  },
  async mounted() {
    this.search = this.$route.query.q || ""
    await this.loadModels(true)
    // Добавляем обработчик скролла на window как fallback
    window.addEventListener("scroll", this.handleWindowScroll, {
      passive: true,
    })
  },
  beforeUnmount() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
      this.scrollTimer = null
    }
    window.removeEventListener("scroll", this.handleWindowScroll)
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  },
  methods: {
    async loadModels(reset = false) {
      if (reset) {
        this.offset = 0
        this.hasMore = true
        this.isLoading = true
        this.isRequestPending = false
        this.lastScrollTime = 0
      } else {
        // Проверяем условия, но НЕ проверяем isRequestPending и isLoadingMore,
        // так как они уже установлены в checkScrollPosition
        if (!this.hasMore || this.isLoading) {
          // Сбрасываем флаги, если не можем загрузить
          this.isRequestPending = false
          this.isLoadingMore = false
          return
        }
        // isLoadingMore уже установлен в checkScrollPosition, не устанавливаем здесь
      }

      const currentOffset = reset ? 0 : this.offset
      // Offset для API - это абсолютное смещение (0, 20, 40, 60...)
      const apiOffset = currentOffset

      try {
        const response = await this.$store.dispatch("fetchModelList", {
          q: this.search?.trim() || undefined,
          limit: this.limit,
          offset: apiOffset,
        })
        const items = Array.isArray(response?.items) ? response.items : []

        if (reset) {
          this.filteredModels = items
        } else {
          this.filteredModels = [...this.filteredModels, ...items]
        }
        // Проверяем, есть ли еще данные для загрузки
        this.hasMore = items.length === this.limit
        // Offset увеличиваем на limit (20) каждый раз
        if (this.hasMore) {
          this.offset = currentOffset + this.limit
        } else {
          this.offset = currentOffset
        }
      } catch (error) {
        if (reset) {
          this.filteredModels = []
        }
        this.hasMore = false
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
        this.isRequestPending = false
      }
    },
    handleScroll(event) {
      this.checkScrollPosition(event.target)
    },
    handleWindowScroll() {
      this.checkScrollPosition(window)
    },
    checkScrollPosition(scrollSource) {
      // Отменяем предыдущий таймер
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }

      // Debounce - проверяем только после остановки скролла
      this.scrollTimer = setTimeout(() => {
        // Сначала пытаемся использовать элемент listSection
        let element = this.$refs.listSection
        let scrollTop, scrollHeight, clientHeight

        if (element && element.scrollHeight > element.clientHeight) {
          // Элемент скроллится
          scrollTop = element.scrollTop
          scrollHeight = element.scrollHeight
          clientHeight = element.clientHeight
        } else if (scrollSource === window || !element) {
          // Используем window
          scrollTop = window.pageYOffset || document.documentElement.scrollTop
          scrollHeight = document.documentElement.scrollHeight
          clientHeight = window.innerHeight
          element = window
        } else {
          // Элемент не скроллится, но есть
          scrollTop = element.scrollTop
          scrollHeight = element.scrollHeight
          clientHeight = element.clientHeight
        }

        const scrollBottom = scrollHeight - scrollTop - clientHeight

        // Загружаем следующую страницу только если доскроллили до конца (200px от низа для большей чувствительности)
        const threshold = 200
        const isNearBottom = scrollBottom <= threshold

        // Защита от частых запросов - минимум 1 секунда между запросами
        const now = Date.now()
        const timeSinceLastRequest = now - this.lastScrollTime

        if (
          isNearBottom &&
          this.hasMore &&
          !this.isLoadingMore &&
          !this.isLoading &&
          !this.isRequestPending &&
          timeSinceLastRequest >= 1000
        ) {
          this.lastScrollTime = now
          this.isRequestPending = true // Устанавливаем флаг ДО вызова loadModels
          this.isLoadingMore = true // Показываем индикатор загрузки сразу
          this.loadModels(false)
        }
      }, 150) // Debounce 150ms
    },
    handleSearchInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      if (!this.search) {
        this.resetSelection()
      }
      this.searchTimer = window.setTimeout(() => {
        this.loadModels(true)
      }, 350)
    },
    clearSearch() {
      this.search = ""
      this.loadModels(true)
      this.resetSelection()
    },
    resetSelection() {
      this.selectedId = null
      this.$store.commit("setSelectedModel", null)
    },
    async selectAndClose(model) {
      // Убеждаемся, что selectedId устанавливается правильно как строка
      this.selectedId = String(model.id)

      // Загружаем полную информацию о модели с avg_prices
      try {
        const modelData = await this.$store.dispatch("getModelData", {
          id: model.id,
        })

        // Объединяем данные модели с avg_prices
        const fullModel = {
          ...model,
          ...(modelData?.data || modelData || {}),
        }

        // Сохраняем модель в store
        this.$store.commit("setSelectedModel", fullModel)
      } catch (error) {
        console.error("Ошибка при загрузке данных модели:", error)
        // В случае ошибки сохраняем хотя бы базовую модель
        this.$store.commit("setSelectedModel", model)
      }

      this.$router.back()
    },
    // «Своя модель»: помечаем режим в store и возвращаемся на экран
    // создания товара, где раскроется блок полей модели.
    addCustomModel() {
      this.$store.commit("setSelectedModel", null)
      this.$store.commit("setCustomModelMode", true)
      this.$router.back()
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 0.625rem 1rem 12rem 1rem;
  min-height: 80vh;
  display: flex;
  background: #f5f6fb;
  flex-direction: column;
  overflow: hidden;
}

.searchSection {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
}

.headerRow {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding-left: 1rem;
}

.sectionLabel {
  font-size: 0.875rem;
  color: #8e8e93;
  font-weight: 400;
}

.searchCard {
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  position: relative;
}

.searchInput {
  width: 100%;
  border: none;
  background: none;
  font-size: 1rem;
  color: #000;
  outline: none;
  padding: 0;
}
.searchInput::placeholder {
  color: #8e8e93;
}

.clearBtn {
  position: absolute;
  right: 1rem;
  border: none;
  background: #eef0f6;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  color: #8e8e93;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.listSection {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-bottom: 1rem;
  gap: 0.625rem;
}

.loadingState {
  background: #fff;
  border-radius: 1rem;
  padding: 0.625rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.skeletonCard {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.skeletonLine {
  height: 0.625rem;
  width: 70%;
  border-radius: 0.5rem;
}
.skeletonLineShort {
  height: 0.625rem;
  width: 30%;
  border-radius: 0.5rem;
}

.emptyState {
  text-align: center;
  color: #9ca0b3;
  margin-top: 1.25rem;
}

.resultsGroup {
  background: #fff;
  border-radius: 0.625rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modelRow {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  background: #fff;
  transition: background 0.2s;
}

.modelRow:active {
  background: #f9f9fa;
}

.modelInfo {
  flex: 1;
  padding-right: 0.625rem;
}

.modelTitle {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.modelSub {
  font-size: 0.875rem;
  color: #8e8e93;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.skuDivider {
  color: #8e8e93;
}

.radioWrapper {
  display: flex;
  align-items: center;
}

.radioCircle {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 1px solid #d1d1d6;
  background: #fff;
}
.radioChecked {
  border-color: #e65c00;
  background: #e65c00;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='10' viewBox='0 0 12 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5L4.5 8.5L11 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

/* --- «Добавить свою модель» --- */
.addTitle {
  font-size: 1rem;
  font-weight: 600;
  color: #e65c00;
  margin-bottom: 0.25rem;
  line-height: 1.2;
}

.chevron {
  font-size: 1.5rem;
  color: #8e8e93;
  line-height: 1;
}

.rowDivider {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 1rem;
  height: 1px;
  background: #f0f0f5;
}

.loadingMore {
  background: #fff;
  border-radius: 1rem;
  padding: 1.5rem 1rem;
  margin-top: 0.625rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.loadingSpinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #f0f0f5;
  border-top-color: #e65c00;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loadingText {
  font-size: 0.875rem;
  color: #8e8e93;
  text-align: center;
}

.loadMoreTrigger {
  height: 1px;
  width: 100%;
}
</style>
